import React, { useEffect, useState } from 'react';
import { ChevronRightIcon, CoinsIcon, BookmarkIcon, PlusIcon, SearchIcon, CheckIcon, XIcon, EditIcon, TrashIcon, SaveIcon } from 'lucide-react';
import { CurrencyList } from './CurrencyList';
import { CurrencyDetail } from './CurrencyDetail';
import { setDefaultCurrency } from '../../../utils/appSettings';
export function CurrencyManagement({
  locale = 'en-US'
}) {
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [currencyToDelete, setCurrencyToDelete] = useState(null);
  // Sample currency data
  const [currencies, setCurrencies] = useState([{
    currencyId: '5',
    code: 'AUD',
    numericCode: '036',
    name: 'Australian Dollar',
    symbol: 'A$',
    countries: ['Australia'],
    isDefault: true,
    decimalPlaces: 2
  }, {
    currencyId: '1',
    code: 'USD',
    numericCode: '840',
    name: 'US Dollar',
    symbol: '$',
    countries: ['United States', 'Ecuador', 'El Salvador'],
    isDefault: false,
    decimalPlaces: 2
  }, {
    currencyId: '2',
    code: 'EUR',
    numericCode: '978',
    name: 'Euro',
    symbol: '€',
    countries: ['Germany', 'France', 'Italy', 'Spain', 'Netherlands', 'Belgium', 'Austria', 'Portugal', 'Ireland', 'Finland'],
    isDefault: false,
    decimalPlaces: 2
  }, {
    currencyId: '3',
    code: 'GBP',
    numericCode: '826',
    name: 'British Pound',
    symbol: '£',
    countries: ['United Kingdom'],
    isDefault: false,
    decimalPlaces: 2
  }, {
    currencyId: '4',
    code: 'JPY',
    numericCode: '392',
    name: 'Japanese Yen',
    symbol: '¥',
    countries: ['Japan'],
    isDefault: false,
    decimalPlaces: 0
  }, {
    currencyId: '6',
    code: 'CAD',
    numericCode: '124',
    name: 'Canadian Dollar',
    symbol: 'C$',
    countries: ['Canada'],
    isDefault: false,
    decimalPlaces: 2
  }, {
    currencyId: '7',
    code: 'CHF',
    numericCode: '756',
    name: 'Swiss Franc',
    symbol: 'Fr',
    countries: ['Switzerland', 'Liechtenstein'],
    isDefault: false,
    decimalPlaces: 2
  }, {
    currencyId: '8',
    code: 'CNY',
    numericCode: '156',
    name: 'Chinese Yuan',
    symbol: '¥',
    countries: ['China'],
    isDefault: false,
    decimalPlaces: 2
  }, {
    currencyId: '9',
    code: 'INR',
    numericCode: '356',
    name: 'Indian Rupee',
    symbol: '₹',
    countries: ['India'],
    isDefault: false,
    decimalPlaces: 2
  }, {
    currencyId: '10',
    code: 'SGD',
    numericCode: '702',
    name: 'Singapore Dollar',
    symbol: 'S$',
    countries: ['Singapore'],
    isDefault: false,
    decimalPlaces: 2
  }]);
  // Filter currencies based on search query
  const filteredCurrencies = currencies.filter(currency => {
    const query = searchQuery.toLowerCase();
    return currency.name.toLowerCase().includes(query) || currency.code.toLowerCase().includes(query) || currency.numericCode.includes(query) || currency.symbol.toLowerCase().includes(query) || currency.countries.some(country => country.toLowerCase().includes(query));
  });
  // Update global default currency when currencies change
  useEffect(() => {
    const defaultCurrency = currencies.find(currency => currency.isDefault);
    if (defaultCurrency) {
      setDefaultCurrency({
        code: defaultCurrency.code,
        name: defaultCurrency.name,
        symbol: defaultCurrency.symbol
      });
    }
  }, [currencies]);
  const handleSelectCurrency = currency => {
    setSelectedCurrency(currency);
    setIsCreating(false);
  };
  const handleCreateCurrency = () => {
    setSelectedCurrency(null);
    setIsCreating(true);
  };
  const handleSaveCurrency = currencyData => {
    if (isCreating) {
      // Generate a unique ID for new currency
      const newCurrencyId = `${currencies.length + 1}`;
      // If setting this as default, update all other currencies
      const updatedCurrencies = currencyData.isDefault ? currencies.map(c => ({
        ...c,
        isDefault: false
      })) : [...currencies];
      // Add new currency
      setCurrencies([...updatedCurrencies, {
        ...currencyData,
        currencyId: newCurrencyId
      }]);
    } else {
      // If setting this as default, update all other currencies
      if (currencyData.isDefault) {
        setCurrencies(currencies.map(currency => ({
          ...currency,
          isDefault: currency.currencyId === currencyData.currencyId ? true : false
        })));
      } else {
        // Check if this was the default currency
        const wasDefault = currencies.find(c => c.currencyId === currencyData.currencyId)?.isDefault;
        // Don't allow removing default status without setting another default
        if (wasDefault) {
          alert('You must have at least one default currency. Please set another currency as default first.');
          return;
        }
        // Update existing currency
        setCurrencies(currencies.map(currency => currency.currencyId === currencyData.currencyId ? currencyData : currency));
      }
    }
    setSelectedCurrency(null);
    setIsCreating(false);
  };
  const handleCancelEdit = () => {
    setSelectedCurrency(null);
    setIsCreating(false);
  };
  const handleDeleteClick = currency => {
    // Don't allow deleting the default currency
    if (currency.isDefault) {
      alert('You cannot delete the default currency. Please set another currency as default first.');
      return;
    }
    setCurrencyToDelete(currency);
    setShowDeleteConfirm(true);
  };
  const confirmDelete = () => {
    setCurrencies(currencies.filter(currency => currency.currencyId !== currencyToDelete.currencyId));
    setShowDeleteConfirm(false);
    setCurrencyToDelete(null);
    if (selectedCurrency && selectedCurrency.currencyId === currencyToDelete.currencyId) {
      setSelectedCurrency(null);
    }
  };
  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setCurrencyToDelete(null);
  };
  // Add handler for breadcrumb navigation
  const handleBreadcrumbClick = level => {
    if (level === 'settings') {
      // In a real app, this would navigate to settings page
      // For now, we'll just go back to currency list
      setSelectedCurrency(null);
      setIsCreating(false);
    } else if (level === 'currencies') {
      setSelectedCurrency(null);
      setIsCreating(false);
    }
  };
  return <div className="space-y-6" data-id="element-17112">
      <div className="mb-8" data-id="element-17113">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-17114">Currencies</h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-17115">
          Manage currencies used throughout the system for transactions,
          budgeting, and reporting
        </p>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && <div className="fixed z-10 inset-0 overflow-y-auto" data-id="element-17116">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-17117">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" data-id="element-17118">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-17119"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-17120">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-17121">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-17122">
                <div className="sm:flex sm:items-start" data-id="element-17123">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10" data-id="element-17124">
                    <XIcon className="h-6 w-6 text-red-600" data-id="element-17125" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left" data-id="element-17126">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title" data-id="element-17127">
                      Delete Currency
                    </h3>
                    <div className="mt-2" data-id="element-17128">
                      <p className="text-sm text-gray-500" data-id="element-17129">
                        Are you sure you want to delete{' '}
                        <strong data-id="element-17130">
                          {currencyToDelete?.name} ({currencyToDelete?.code})
                        </strong>
                        ? This action cannot be undone, and may affect any
                        records that reference this currency.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-17131">
                <button type="button" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm" onClick={confirmDelete} data-id="element-17132">
                  Delete
                </button>
                <button type="button" className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" onClick={cancelDelete} data-id="element-17133">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
      {/* Currency Detail or List */}
      {selectedCurrency || isCreating ? <CurrencyDetail currency={selectedCurrency} onSave={handleSaveCurrency} onCancel={handleCancelEdit} isCreating={isCreating} locale={locale} data-id="element-17134" /> : <>
          <div className="flex justify-between items-center mb-4" data-id="element-17135">
            <div className="relative" data-id="element-17136">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-17137">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-17138" />
              </div>
              <input type="text" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-3 py-2 sm:text-sm border-gray-300 rounded-md" placeholder="Search currencies..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-17139" />
            </div>
            <button type="button" onClick={handleCreateCurrency} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17140">
              <PlusIcon className="h-4 w-4 mr-2" data-id="element-17141" />
              Add Currency
            </button>
          </div>
          <CurrencyList currencies={filteredCurrencies} onSelectCurrency={handleSelectCurrency} onDeleteCurrency={handleDeleteClick} locale={locale} data-id="element-17142" />
        </>}
    </div>;
}